import graphql from "babel-plugin-relay/macro";
import {
  endOfMonth,
  format,
  isAfter,
  isBefore,
  parse,
  startOfMonth,
} from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchQuery, useRelayEnvironment } from "react-relay";

import { normalizeSliderTimeValue } from "@/components/AgendaFilters/utils/getSliderOptions";
import { DayObject } from "@whoosh/design-system/src/components/controls/Datepickers/components/Calendar/types";
import { generateDateInfo } from "@whoosh/design-system/src/components/controls/Datepickers/components/Calendar/utils/calendarDateUtils";

import { useDateSelectorAvailabilityDateInfoQuery } from "./__generated__/useDateSelectorAvailabilityDateInfoQuery.graphql";

const mergeDayInfoMaps = (
  oldDateInfo: Map<string, DayObject>,
  newDateInfo: Map<string, DayObject>
) => {
  const merged = new Map(oldDateInfo);
  newDateInfo.forEach((dayObject, date) => {
    merged.set(date, dayObject);
  });
  return merged;
};

export const useDateSelectorAvailabilityDateInfoQueryString = graphql`
  query useDateSelectorAvailabilityDateInfoQuery(
    $facilityId: ID!
    $startDate: Date!
    $endDate: Date!
    $minDuration: Int!
    $maxDuration: Int!
    $courseBucketIds: [ID!]
    $groupSize: Int
    $startTime: Time
    $endTime: Time
  ) {
    node(id: $facilityId) {
      ... on Facility {
        club {
          ...useGetClubStartOfWeekNumberPatron_club
        }
        agendas(dateGte: $startDate, dateLte: $endDate) {
          edges {
            node {
              id
              date
              availableTimes(
                courseBucketIds: $courseBucketIds
                minDuration: $minDuration
                maxDuration: $maxDuration
                first: 1
                playerCount: $groupSize
                timeGte: $startTime
                timeLte: $endTime
              ) {
                time
              }
            }
          }
        }
      }
    }
  }
`;

type UseDateSelectorAvailabilityDateInfoArgs = {
  displayedMonthStartDate: string;
  facilityId: string;
  startDate: string;
  endDate: string;
  groupSize?: number;
  courseBucketIds?: readonly string[];
  startTime?: string;
  endTime?: string;
  minDuration: number;
  maxDuration: number;
};

export const useDateSelectorAvailabilityDateInfo = ({
  displayedMonthStartDate,
  facilityId,
  startDate,
  endDate,
  groupSize,
  courseBucketIds,
  startTime,
  endTime,
  minDuration,
  maxDuration,
}: UseDateSelectorAvailabilityDateInfoArgs) => {
  const relayEnvironment = useRelayEnvironment();
  const [dateInfo, setDateInfo] = useState<Map<string, DayObject>>(new Map());

  const loadedMonthKeysRef = useRef(new Set<string>());
  const requestIdRef = useRef(0);

  const courseBucketIdsKey = (courseBucketIds ?? []).join(",");
  const normalizedCourseBucketIds = useMemo(
    () => (courseBucketIds?.length ? [...courseBucketIds] : undefined),
    [courseBucketIds]
  );
  const parsedCalendarStartDate = parse(startDate, "yyyy-MM-dd", new Date());
  const parsedCalendarEndDate = parse(endDate, "yyyy-MM-dd", new Date());
  const displayedMonthDate = parse(
    displayedMonthStartDate,
    "yyyy-MM-dd",
    new Date()
  );

  const monthStartDate = startOfMonth(displayedMonthDate);
  const monthEndDate = endOfMonth(displayedMonthDate);

  const boundedMonthStartDate = isBefore(
    monthStartDate,
    parsedCalendarStartDate
  )
    ? parsedCalendarStartDate
    : monthStartDate;
  const boundedMonthEndDate = isAfter(monthEndDate, parsedCalendarEndDate)
    ? parsedCalendarEndDate
    : monthEndDate;
  const hasVisibleDatesInRange = !isAfter(
    boundedMonthStartDate,
    boundedMonthEndDate
  );
  const boundedMonthStartDateString = format(
    boundedMonthStartDate,
    "yyyy-MM-dd"
  );
  const boundedMonthEndDateString = format(boundedMonthEndDate, "yyyy-MM-dd");
  const displayedMonthKey = format(monthStartDate, "yyyy-MM");
  const normalizedStartTime = normalizeSliderTimeValue(startTime);
  const normalizedEndTime = normalizeSliderTimeValue(endTime);

  useEffect(() => {
    loadedMonthKeysRef.current.clear();
    requestIdRef.current += 1;
    setDateInfo(new Map());
  }, [
    facilityId,
    startDate,
    endDate,
    groupSize,
    startTime,
    endTime,
    minDuration,
    maxDuration,
    courseBucketIdsKey,
  ]);

  useEffect(() => {
    if (!hasVisibleDatesInRange) return;
    if (loadedMonthKeysRef.current.has(displayedMonthKey)) return;

    const boundedMonthStart = parse(
      boundedMonthStartDateString,
      "yyyy-MM-dd",
      new Date()
    );
    const boundedMonthEnd = parse(
      boundedMonthEndDateString,
      "yyyy-MM-dd",
      new Date()
    );

    const loadingDateInfo = generateDateInfo(
      boundedMonthStart,
      boundedMonthEnd,
      "AvailabilityLoading"
    );
    setDateInfo((previousDateInfo) =>
      mergeDayInfoMaps(previousDateInfo, loadingDateInfo)
    );

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    const subscription = fetchQuery<useDateSelectorAvailabilityDateInfoQuery>(
      relayEnvironment,
      useDateSelectorAvailabilityDateInfoQueryString,
      {
        facilityId,
        startDate: boundedMonthStartDateString,
        endDate: boundedMonthEndDateString,
        groupSize,
        courseBucketIds: normalizedCourseBucketIds,
        startTime: normalizedStartTime,
        endTime: normalizedEndTime,
        minDuration,
        maxDuration,
      },
      {
        fetchPolicy: "network-only",
      }
    ).subscribe({
      next: (response) => {
        if (requestId !== requestIdRef.current) return;

        const monthAvailability = generateDateInfo(
          boundedMonthStart,
          boundedMonthEnd,
          "Unavailable"
        );

        response.node?.agendas?.edges?.forEach((agenda) => {
          if (!agenda?.node?.date) return;
          const hasAvailability =
            agenda.node.availableTimes && agenda.node.availableTimes.length > 0;
          monthAvailability.set(agenda.node.date, {
            status: hasAvailability ? "Available" : "Unavailable",
          });
        });

        loadedMonthKeysRef.current.add(displayedMonthKey);
        setDateInfo((previousDateInfo) =>
          mergeDayInfoMaps(previousDateInfo, monthAvailability)
        );
      },
      error: () => {
        if (requestId !== requestIdRef.current) return;
        loadedMonthKeysRef.current.add(displayedMonthKey);
        setDateInfo((previousDateInfo) =>
          mergeDayInfoMaps(
            previousDateInfo,
            generateDateInfo(boundedMonthStart, boundedMonthEnd, "Unavailable")
          )
        );
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [
    boundedMonthEndDateString,
    boundedMonthStartDateString,
    courseBucketIdsKey,
    displayedMonthKey,
    facilityId,
    groupSize,
    hasVisibleDatesInRange,
    maxDuration,
    minDuration,
    normalizedEndTime,
    normalizedCourseBucketIds,
    normalizedStartTime,
    relayEnvironment,
  ]);

  return dateInfo;
};
